/**
 * Acceso a IndexedDB para el modo offline de la app móvil.
 *
 * Envoltorio mínimo sobre la API nativa: IndexedDB es callback-based y usarla
 * cruda desde React es una fuente garantizada de errores silenciosos. No se
 * suma una dependencia (idb, dexie) porque lo que necesitamos son cinco
 * operaciones y el envoltorio entra en un archivo.
 *
 * Aislamiento por tenant: cada tenant vive en su propio subdominio, así que el
 * navegador ya le da una base separada por origen. Aun así se guarda el slug en
 * `meta` y se valida al abrir — en desarrollo todos los tenants comparten
 * `localhost` y sin esa validación los catálogos de uno se le mostrarían a otro.
 */

const NOMBRE_DB = 'fccloud-offline';
const VERSION_DB = 1;

export const STORE_CATALOGOS = 'catalogos';
export const STORE_OUTBOX = 'outbox';
export const STORE_META = 'meta';

/** Estados posibles de un envío en la cola. */
export const ESTADO_PENDIENTE = 'pendiente';   // esperando red
export const ESTADO_ENVIANDO = 'enviando';     // request en vuelo
export const ESTADO_RECHAZADO = 'rechazado';   // el backend lo rechazó: requiere corrección humana
export const ESTADO_CONFIRMADO = 'confirmado'; // aceptado por el backend

let promesaDB = null;

/**
 * Abre (y crea/migra si hace falta) la base local.
 * La promesa se cachea: abrir IndexedDB en cada llamada es caro y además
 * dispara `onupgradeneeded` en paralelo si dos componentes montan a la vez.
 */
export function abrirDB() {
  if (promesaDB) return promesaDB;

  promesaDB = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('Este navegador no soporta almacenamiento local (IndexedDB).'));
      return;
    }

    const solicitud = indexedDB.open(NOMBRE_DB, VERSION_DB);

    solicitud.onupgradeneeded = (evento) => {
      const db = evento.target.result;

      if (!db.objectStoreNames.contains(STORE_CATALOGOS)) {
        // Un registro por catálogo completo ('equipos', 'obras', ...). Guardar
        // el catálogo entero en una fila y no fila por fila es a propósito: se
        // lee siempre completo para poblar un desplegable, y así el reemplazo
        // tras sincronizar es atómico en vez de un diff registro por registro.
        db.createObjectStore(STORE_CATALOGOS, { keyPath: 'clave' });
      }

      if (!db.objectStoreNames.contains(STORE_OUTBOX)) {
        const outbox = db.createObjectStore(STORE_OUTBOX, { keyPath: 'id' });
        outbox.createIndex('estado', 'estado', { unique: false });
        outbox.createIndex('creado_en', 'creado_en', { unique: false });
      }

      if (!db.objectStoreNames.contains(STORE_META)) {
        db.createObjectStore(STORE_META, { keyPath: 'clave' });
      }
    };

    solicitud.onsuccess = () => resolve(solicitud.result);
    solicitud.onerror = () => reject(solicitud.error);
    solicitud.onblocked = () => reject(new Error('La base local quedó bloqueada por otra pestaña abierta.'));
  });

  // Si falla la apertura no se cachea el rechazo: un fallo transitorio (Safari
  // en modo privado, cuota momentánea) dejaría la app sin almacenamiento para
  // siempre hasta recargar.
  promesaDB.catch(() => { promesaDB = null; });

  return promesaDB;
}

/** Envuelve un IDBRequest en promesa. */
function comoPromesa(solicitud) {
  return new Promise((resolve, reject) => {
    solicitud.onsuccess = () => resolve(solicitud.result);
    solicitud.onerror = () => reject(solicitud.error);
  });
}

/**
 * Corre `operacion` dentro de una transacción y resuelve cuando la transacción
 * termina de verdad (`oncomplete`), no cuando resuelve el request. La
 * diferencia importa: una escritura puede resolver y después la transacción
 * abortar por cuota, y sin esperar el `oncomplete` daríamos por guardado algo
 * que se perdió — justo el fallo que no podemos permitirnos con un parte
 * cargado en el campo.
 */
export async function enTransaccion(stores, modo, operacion) {
  const db = await abrirDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(stores, modo);
    let resultado;
    tx.oncomplete = () => resolve(resultado);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error('Transacción cancelada.'));

    Promise.resolve(operacion(tx, comoPromesa))
      .then((valor) => { resultado = valor; })
      .catch((error) => {
        try { tx.abort(); } catch { /* la transacción ya estaba cerrada */ }
        reject(error);
      });
  });
}

// ─── Catálogos ────────────────────────────────────────────────────────────────

/** Devuelve el catálogo `clave` o `[]` si nunca se sincronizó. */
export async function leerCatalogo(clave) {
  return enTransaccion([STORE_CATALOGOS], 'readonly', async (tx, prom) => {
    const fila = await prom(tx.objectStore(STORE_CATALOGOS).get(clave));
    return fila?.datos ?? [];
  });
}

/** Reemplaza por completo el catálogo `clave`. */
export async function guardarCatalogo(clave, datos) {
  return enTransaccion([STORE_CATALOGOS], 'readwrite', async (tx, prom) => {
    await prom(tx.objectStore(STORE_CATALOGOS).put({
      clave,
      datos,
      actualizado_en: new Date().toISOString(),
    }));
  });
}

/**
 * Reemplaza varios catálogos en una sola transacción, para que la app nunca
 * quede con equipos nuevos y obras viejas si la escritura se corta a la mitad.
 */
export async function guardarCatalogos(porClave) {
  const actualizado_en = new Date().toISOString();
  return enTransaccion([STORE_CATALOGOS], 'readwrite', async (tx, prom) => {
    const store = tx.objectStore(STORE_CATALOGOS);
    for (const [clave, datos] of Object.entries(porClave)) {
      await prom(store.put({ clave, datos, actualizado_en }));
    }
  });
}

// ─── Metadatos ────────────────────────────────────────────────────────────────

export async function leerMeta(clave, porDefecto = null) {
  return enTransaccion([STORE_META], 'readonly', async (tx, prom) => {
    const fila = await prom(tx.objectStore(STORE_META).get(clave));
    return fila === undefined ? porDefecto : fila.valor;
  });
}

export async function guardarMeta(clave, valor) {
  return enTransaccion([STORE_META], 'readwrite', async (tx, prom) => {
    await prom(tx.objectStore(STORE_META).put({ clave, valor }));
  });
}

/**
 * Borra catálogos y metadatos, dejando la cola de envíos intacta.
 *
 * Se llama al detectar cambio de tenant o de usuario. La cola NO se toca a
 * propósito: son cargas hechas en el campo que todavía no llegaron al
 * servidor, y borrarlas por un cambio de sesión sería perder trabajo real sin
 * que nadie se entere.
 */
export async function limpiarDatosDeSesion() {
  return enTransaccion([STORE_CATALOGOS, STORE_META], 'readwrite', async (tx, prom) => {
    await prom(tx.objectStore(STORE_CATALOGOS).clear());
    await prom(tx.objectStore(STORE_META).clear());
  });
}
